import { useState } from 'react';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (imageUrl: string) => void;
  caption?: string;
  onCaptionChange?: (caption: string) => void;
}

export function ImageUploadModal({ isOpen, onClose, onInsert, caption = '', onCaptionChange }: ImageUploadModalProps) {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [captionState, setCaption] = useState(caption);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const { mutateAsync: uploadFile } = useFileUpload();

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInsert = async () => {
    if (activeTab === 'upload' && imageFile) {
      try {
        setUploading(true);
        const response = await uploadFile({ file: imageFile, category: 'IMAGE' });

        if (response.success && response.data?.fileUrl) {
          showToast.success('Image uploaded successfully!');
          onInsert(response.data.fileUrl);
          handleClose();
        } else {
          throw new Error('Upload failed: No file URL returned');
        }
      } catch (error) {
        console.error('Upload error:', error);
        showToast.error(error instanceof Error ? error.message : 'Failed to upload image');
      } finally {
        setUploading(false);
      }
    } else if (activeTab === 'url' && imageUrl) {
      onInsert(imageUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setImageUrl('');
    setImageFile(null);
    setPreview('');
    setActiveTab('upload');
    if(captionState && onCaptionChange) {
      onCaptionChange(captionState);
    } else {
      onCaptionChange?.('');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Insert Image</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'text-forestGreen-100 border-b-2 border-forestGreen-100'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Upload from Device
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'url'
                ? 'text-forestGreen-100 border-b-2 border-forestGreen-100'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Insert from URL
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'upload' ? (
            <div>
              {preview ? (
                <div className="space-y-4">
                  <img src={preview} alt="Preview" className="w-full h-64 object-cover rounded-lg" />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image Caption (optional)</label>
                    <input
                      type="text"
                      value={captionState}
                      onChange={(e) =>{
                        setCaption(e.target.value);
                        onCaptionChange?.(e.target.value);
                      }}
                      placeholder="Enter image caption"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setPreview('');
                      setImageFile(null);
                      onCaptionChange?.('');
                    }}
                    className="text-sm text-red-600 hover:text-red-700 border border-red-600 hover:border-red-700 rounded-lg px-3 py-1 transition-colors"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="modalImageUpload"
                  />
                  <label
                    htmlFor="modalImageUpload"
                    className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none"
                />
              </div>
              {imageUrl && (
                <>
                  <div>
                    <img src={imageUrl} alt="Preview" className="w-full h-64 object-cover rounded-lg" onError={() => setImageUrl('')} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image Caption (optional)</label>
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => onCaptionChange?.(e.target.value)}
                      placeholder="Enter image caption"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={uploading || (!preview && !imageUrl)}
            className="px-4 py-2 text-sm font-medium text-white bg-forestGreen-100 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Insert Image'}
          </button>
        </div>
      </div>
    </div>
  );
}
